/**
 * Panneau d'amélioration d'une tour posée (M2-07).
 *
 * Le joueur touche une tour sur le parcours : ce panneau propose de la faire monter d'un niveau.
 * Le niveau atteint et le débit sont décidés par le serveur (`POST /runs/:id/towers/:towerId/upgrade`,
 * corps vide) ; l'écran n'envoie donc **aucun** niveau ni aucun montant (DECISIONS §8.8).
 *
 * Le prix affiché est le **coût de base du contenu** (`coutParNiveau × niveau cible`, DECISIONS
 * A-6). Les cartes de la run peuvent le réduire : le solde réellement débité est celui que le
 * serveur renvoie dans la réponse de pose/amélioration, jamais un calcul local.
 */
import { useTranslation } from 'react-i18next';

import { cleTraductionContenu, formaterNombre } from '../i18n';
import { Bouton } from './Bouton';

import type { TourServeur } from '../ui/game-canvas/types';
import type { Tour } from '@elemora/shared/content';

/** Niveau maximal d'une tour (contenu, DECISIONS §6.3). */
export const NIVEAU_MAX_TOUR = 3;

export interface ProprietesPanneauAmelioration {
  readonly tour: TourServeur;
  /** Définition de contenu de la tour (nom, coût de base). */
  readonly definition: Tour;
  readonly enCours: boolean;
  /** `false` pendant une vague : le serveur refuse l'amélioration à ce moment-là. */
  readonly ameliorationPossible: boolean;
  readonly onAmeliorer: () => void;
  readonly onFermer: () => void;
}

export function PanneauAmelioration({
  tour,
  definition,
  enCours,
  ameliorationPossible,
  onAmeliorer,
  onFermer,
}: ProprietesPanneauAmelioration) {
  const { t, i18n } = useTranslation();
  const nom = t(cleTraductionContenu(definition.nameKey));
  const auMaximum = tour.niveau >= NIVEAU_MAX_TOUR;
  const coutBase = definition.ameliorations.coutParNiveau * (tour.niveau + 1);

  return (
    <section className="panneau panneau--amelioration" data-amelioration={tour.towerId}>
      <h2 className="panneau__titre">{nom}</h2>
      <dl className="hud__grille">
        <div className="hud__case">
          <dt className="hud__etiquette">{t('jeu.amelioration.niveau')}</dt>
          <dd className="hud__valeur">
            {t('jeu.amelioration.niveauValeur', {
              niveau: formaterNombre(tour.niveau, i18n.language),
              maximum: formaterNombre(NIVEAU_MAX_TOUR, i18n.language),
            })}
          </dd>
        </div>
        {auMaximum ? null : (
          <div className="hud__case">
            <dt className="hud__etiquette">{t('jeu.amelioration.coutBase')}</dt>
            <dd className="hud__valeur">
              {t('jeu.tours.cout', { cout: formaterNombre(coutBase, i18n.language) })}
            </dd>
          </div>
        )}
      </dl>

      {auMaximum ? (
        <p className="note">{t('jeu.amelioration.maximum')}</p>
      ) : ameliorationPossible ? (
        <Bouton variante="principal" enCours={enCours} onClick={onAmeliorer}>
          {t('jeu.amelioration.action', { niveau: tour.niveau + 1 })}
        </Bouton>
      ) : (
        <p className="note note--alerte">{t('jeu.amelioration.pendantVague')}</p>
      )}

      <Bouton variante="secondaire" onClick={onFermer}>
        {t('jeu.amelioration.fermer')}
      </Bouton>
    </section>
  );
}
